import React from 'react';

export type TypographyVariant = 
  | 'h1' 
  | 'h2' 
  | 'h3' 
  | 'h4' 
  | 'h5' 
  | 'h6'
  | 'body-lg'
  | 'body'
  | 'body-sm'
  | 'caption'
  | 'overline'
  | 'button'
  | 'link';

export type TypographyWeight = 'normal' | 'medium' | 'semibold' | 'bold';

export type TypographyProps = {
  variant: TypographyVariant;
  weight?: TypographyWeight;
  children: React.ReactNode;
  className?: string;
  as?: React.ElementType;
  color?: 'primary' | 'secondary' | 'muted' | 'accent' | 'white' | 'inherit';
  align?: 'left' | 'center' | 'right' | 'justify';
  truncate?: boolean;
  noWrap?: boolean;
  [key: string]: any; // Allow additional props for elements like href, etc.
};

const Typography: React.FC<TypographyProps> = ({
  variant,
  weight,
  children,
  className = '',
  as,
  color = 'primary',
  align = 'left',
  truncate = false,
  noWrap = false,
  ...rest
}) => {
  // Default element mapping
  const getDefaultElement = (): React.ElementType => {
    switch (variant) {
      case 'h1':
      case 'h2':
      case 'h3':
      case 'h4':
      case 'h5':
      case 'h6':
        return variant;
      case 'body-lg':
      case 'body':
      case 'body-sm':
      case 'caption':
      case 'overline':
        return 'p';
      case 'button':
        return 'span';
      case 'link':
        return 'span';
      default:
        return 'p';
    }
  };

  // Base classes
  const baseClasses = [
    // Alignment
    `text-${align}`,
    // Truncation
    truncate ? 'truncate' : '',
    noWrap ? 'whitespace-nowrap' : '',
    // Color variants
    color === 'primary' && 'text-gray-900 dark:text-white',
    color === 'secondary' && 'text-gray-700 dark:text-gray-300',
    color === 'muted' && 'text-gray-600 dark:text-gray-400',
    color === 'accent' && 'text-teal-600 dark:text-teal-400',
    color === 'white' && 'text-white',
    color === 'inherit' && 'text-inherit',
    // Weight
    weight === 'normal' && 'font-normal',
    weight === 'medium' && 'font-medium',
    weight === 'semibold' && 'font-semibold',
    weight === 'bold' && 'font-bold',
    className,
  ].filter(Boolean).join(' ');

  // Variant-specific classes
  const getVariantClasses = (): string => {
    switch (variant) {
      case 'h1':
        return 'text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight';
      case 'h2':
        return 'text-3xl md:text-4xl font-bold tracking-tight';
      case 'h3':
        return 'text-2xl md:text-3xl font-semibold';
      case 'h4':
        return 'text-xl md:text-2xl font-semibold';
      case 'h5':
        return 'text-lg md:text-xl font-medium';
      case 'h6':
        return 'text-base md:text-lg font-medium';
      case 'body-lg':
        return 'text-lg leading-relaxed';
      case 'body':
        return 'text-base leading-relaxed';
      case 'body-sm':
        return 'text-sm leading-relaxed';
      case 'caption':
        return 'text-xs leading-relaxed';
      case 'overline':
        return 'text-base uppercase tracking-wider';
      case 'button':
        return 'text-sm font-medium';
      case 'link':
        return 'text-sm font-medium hover:underline cursor-pointer';
      default:
        return 'text-base';
    }
  };

  const Element = as || getDefaultElement();
  const variantClasses = getVariantClasses();

  return React.createElement(Element, {
    className: `${variantClasses} ${baseClasses}`,
    children,
    ...rest
  });
};

export default Typography; 